import type { BoxProps } from '@chakra-ui/react';
import { Box } from '@chakra-ui/react';
import { sub } from 'date-fns';
import type { DateObj } from 'dayzed';
import { useDayzed } from 'dayzed';
import type { FC, SyntheticEvent } from 'react';
import { useState } from 'react';

import type { SelectionStatus } from './CalendarBody';
import { CalendarBody } from './CalendarBody';
import { CalendarYearBar } from './CalendarYearBar';

export type CalendarProps = BoxProps & {
  maxDate?: Date;
  minDate?: Date;
  selected: Date[] | Date | undefined;
  dateForInitialView?: Date;
  onDateSelected(selectedDate: DateObj, event: SyntheticEvent): void;
  onSelectionStatus: (dateObj: DateObj) => SelectionStatus;
  onMouseMoveOnCell?: (dateObj: DateObj) => void;
};

export const Calendar: FC<CalendarProps> = ({
  selected,
  onDateSelected,
  dateForInitialView = new Date(),
  minDate = sub(new Date(), {
    years: 5,
  }),
  maxDate = new Date(),
  onSelectionStatus,
  onMouseMoveOnCell,
  ...props
}) => {
  const [dayzedOffset, setDayzedOffset] = useState<number>(0);
  const dayzedData = useDayzed({
    date: dateForInitialView,
    selected,
    onDateSelected,
    offset: dayzedOffset,
    onOffsetChanged: setDayzedOffset,
    minDate,
    maxDate,
  });
  const yearOfCalendar =
    dayzedData.calendars[0]?.year || new Date().getFullYear();
  const monthOfCalendar =
    dayzedData.calendars[0]?.month || new Date().getMonth();

  return (
    <Box {...props}>
      <CalendarYearBar
        minDate={minDate}
        maxDate={maxDate}
        year={yearOfCalendar}
        onYearChange={(year) => {
          const offsetOfMonth = monthOfCalendar - dateForInitialView.getMonth();
          setDayzedOffset(
            (year - dateForInitialView.getFullYear()) * 12 + offsetOfMonth,
          );
        }}
        mb="12px"
        px="16px"
      />
      <CalendarBody
        {...dayzedData}
        onSelectionStatus={onSelectionStatus}
        onMouseMoveOnCell={onMouseMoveOnCell}
      />
    </Box>
  );
};
